import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import jobsData from '../data/jobsData';
import { slugify } from '../utils/slugify';

const Jobs = () => {
  const [jobs] = useState(jobsData);
  const [filteredJobs, setFilteredJobs] = useState(jobsData);
  const [filters, setFilters] = useState({
    keyword: '',
    location: '',
    category: '',
    jobType: [],
    experience: [],
    salary: []
  });

  // Filter jobs based on filters
  const applyFilters = () => {
    let result = jobs.filter(job => {
      // Keyword filter
      const matchesKeyword = !filters.keyword || 
        job.title.toLowerCase().includes(filters.keyword.toLowerCase()) ||
        job.company.toLowerCase().includes(filters.keyword.toLowerCase()) ||
        job.description.toLowerCase().includes(filters.keyword.toLowerCase());
      
      // Location filter
      const matchesLocation = !filters.location || 
        job.location.toLowerCase().includes(filters.location.toLowerCase());
      
      // Category filter (not implemented in our data, so always matches for now)
      // In a real app, we would have categories in our job data
      const matchesCategory = true;
      
      // Job type filter
      const matchesJobType = filters.jobType.length === 0 || 
        filters.jobType.includes(job.type.toLowerCase().replace(' ', '-'));
      
      // Experience filter (we don't have this field in our mock data yet, so always matches)
      const matchesExperience = true;
      
      // Salary filter
      const matchesSalary = filters.salary.length === 0 || 
        filters.salary.some(range => {
          const [min, max] = range.split('-').map(Number);
          const salaryText = job.salary;
          const salaryMatch = salaryText.match(/AED (\d+,\d+) - (\d+,\d+)/);
          if (salaryMatch) {
            const minSalary = parseInt(salaryMatch[1].replace(',', ''));
            const maxSalary = parseInt(salaryMatch[2].replace(',', ''));
            return min <= minSalary && max >= maxSalary;
          }
          return false;
        });
      
      return matchesKeyword && matchesLocation && matchesCategory && 
             matchesJobType && matchesExperience && matchesSalary;
    });
    
    setFilteredJobs(result);
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFilters(prev => {
        const currentValues = prev[name] || [];
        let newValues;
        
        if (checked) {
          newValues = [...currentValues, value];
        } else {
          newValues = currentValues.filter(v => v !== value);
        }
        
        return { ...prev, [name]: newValues };
      });
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submission
  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      keyword: '',
      location: '',
      category: '',
      jobType: [],
      experience: [],
      salary: []
    });
    setFilteredJobs(jobs);
  };

  return (
    <>
      {/* Page Header */}
      <section className="relative py-24 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920&q=80')" }}>
        {/* Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Browse All Jobs</h1>
            <nav className="flex items-center justify-center space-x-2 text-lg">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors duration-200">Home</Link>
              <i className="fas fa-chevron-right text-gray-400"></i>
              <span className="text-white">All Jobs</span>
            </nav>
          </div>
        </div>
      </section>

      {/* Job Search Section */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
            <form onSubmit={handleSearch}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Keywords Input */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="fas fa-search text-gray-400"></i>
                  </div>
                  <input 
                    type="text" 
                    name="keyword"
                    value={filters.keyword}
                    onChange={handleFilterChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200"
                    placeholder="Job title, keywords, or company" />
                </div>

                {/* Location Select */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="fas fa-map-marker-alt text-gray-400"></i>
                  </div>
                  <select 
                    name="location"
                    value={filters.location}
                    onChange={handleFilterChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 appearance-none bg-white">
                    <option value="">All Locations</option>
                    <option value="Dubai">Dubai</option>
                    <option value="Abu Dhabi">Abu Dhabi</option>
                    <option value="Sharjah">Sharjah</option>
                    <option value="Ajman">Ajman</option>
                    <option value="Ras Al Khaimah">Ras Al Khaimah</option>
                    <option value="Fujairah">Fujairah</option>
                    <option value="Umm Al Quwain">Umm Al Quwain</option>
                  </select>
                </div>

                {/* Category Select */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="fas fa-briefcase text-gray-400"></i>
                  </div>
                  <select 
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all duration-200 appearance-none bg-white">
                    <option value="">All Categories</option>
                    <option value="accounting">Accounting & Finance</option>
                    <option value="admin">Admin & Office</option>
                    <option value="customer-service">Customer Service</option>
                    <option value="engineering">Engineering</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="hospitality">Hospitality & Tourism</option>
                    <option value="it">IT & Software</option>
                    <option value="marketing">Marketing & PR</option>
                    <option value="retail">Retail & Sales</option>
                    <option value="transportation">Transportation & Logistics</option>
                  </select>
                </div>

                {/* Search Button */}
                <button 
                  type="submit"
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
                  Search Jobs
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Jobs Listing Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Filters Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8 lg:mb-0">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Filter Jobs</h3>
                  <button className="lg:hidden inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <i className="fas fa-filter mr-2"></i> Filters
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Job Type Filter */}
                  <div className="border-b border-gray-200 pb-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">Job Type</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="jobType" 
                          value="full-time" 
                          checked={filters.jobType.includes('full-time')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Full Time</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="jobType" 
                          value="part-time" 
                          checked={filters.jobType.includes('part-time')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Part Time</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="jobType" 
                          value="contract" 
                          checked={filters.jobType.includes('contract')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Contract</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="jobType" 
                          value="internship" 
                          checked={filters.jobType.includes('internship')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Internship</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="jobType" 
                          value="remote" 
                          checked={filters.jobType.includes('remote')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Remote</span>
                      </label>
                    </div>
                  </div>

                  {/* Experience Level Filter */}
                  <div className="border-b border-gray-200 pb-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">Experience Level</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="experience" 
                          value="entry" 
                          checked={filters.experience.includes('entry')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Entry Level</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="experience" 
                          value="mid" 
                          checked={filters.experience.includes('mid')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Mid Level</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="experience" 
                          value="senior" 
                          checked={filters.experience.includes('senior')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Senior Level</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="experience" 
                          value="manager" 
                          checked={filters.experience.includes('manager')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Manager</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="experience" 
                          value="executive" 
                          checked={filters.experience.includes('executive')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Executive</span>
                      </label>
                    </div>
                  </div>

                  {/* Salary Range Filter */}
                  <div className="border-b border-gray-200 pb-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">Salary Range</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="salary" 
                          value="0-5000" 
                          checked={filters.salary.includes('0-5000')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">AED 0 - 5,000</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="salary" 
                          value="5000-10000" 
                          checked={filters.salary.includes('5000-10000')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">AED 5,000 - 10,000</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="salary" 
                          value="10000-15000" 
                          checked={filters.salary.includes('10000-15000')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">AED 10,000 - 15,000</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="salary" 
                          value="15000-25000" 
                          checked={filters.salary.includes('15000-25000')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">AED 15,000 - 25,000</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="salary" 
                          value="25000+" 
                          checked={filters.salary.includes('25000+')}
                          onChange={handleFilterChange}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">AED 25,000+</span>
                      </label>
                    </div>
                  </div>

                  {/* Date Posted Filter */}
                  <div className="pb-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-4">Date Posted</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="date_posted" 
                          value="today" 
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Today</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="date_posted" 
                          value="3days" 
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Last 3 days</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="date_posted" 
                          value="week" 
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Last week</span>
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="checkbox" 
                          name="date_posted" 
                          value="month" 
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                        <span className="ml-3 text-sm text-gray-700">Last month</span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <button 
                      type="button"
                      onClick={applyFilters}
                      className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200">
                      Apply Filters
                    </button>
                    <button 
                      type="button"
                      onClick={resetFilters}
                      className="w-full border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium py-2 px-4 rounded-lg transition-colors duration-200">
                      Reset Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Jobs Content */}
            <div className="lg:col-span-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                <div className="mb-4 sm:mb-0">
                  <p className="text-gray-600">Showing <strong className="text-gray-900">{filteredJobs.length}</strong> of <strong className="text-gray-900">{jobs.length}</strong> jobs</p>
                </div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="sort-by" className="text-sm font-medium text-gray-700">Sort by:</label>
                  <select id="sort-by" name="sort-by" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none">
                    <option value="relevance">Relevance</option>
                    <option value="date-desc">Newest</option>
                    <option value="date-asc">Oldest</option>
                    <option value="salary-desc">Salary (High to Low)</option>
                    <option value="salary-asc">Salary (Low to High)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-6">
                {filteredJobs.length > 0 ? (
                  filteredJobs.map((job) => (
                    <div 
                      key={job.id} 
                      className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer" 
                      onClick={() => {
                        // Convert job title to slug
                        const slug = slugify(job.title);
                        window.location.href = `/job-details/${slug}`;
                      }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <img 
                              src={`https://placehold.co/80x80?text=${job.company.charAt(0)}`} 
                              alt={`${job.company} Logo`} 
                              className="w-12 h-12 object-contain bg-primary-100 rounded text-primary-600 font-bold" 
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                              <Link to={`/job-details/${slugify(job.title)}`} className="hover:text-primary-600 transition-colors">
                                {job.title}
                              </Link>
                            </h3>
                            <div className="flex items-center text-gray-600 mb-3">
                              <i className="fas fa-building mr-2"></i>
                              <span>
                                <Link to={`/organization/${slugify(job.company)}`} className="hover:text-primary-600 transition-colors">
                                  {job.company}
                                </Link>
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          job.type === 'Full Time' ? 'bg-green-100 text-green-800' : 
                          job.type === 'Part Time' ? 'bg-blue-100 text-blue-800' : 
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {job.type}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center text-gray-600 text-sm">
                          <i className="fas fa-map-marker-alt mr-2 w-4"></i>
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center text-gray-600 text-sm">
                          <i className="fas fa-money-bill-wave mr-2 w-4"></i>
                          <span>{job.salary}</span>
                        </div>
                        <div className="flex items-center text-gray-600 text-sm">
                          <i className="fas fa-clock mr-2 w-4"></i>
                          <span>Posted {job.posted}</span>
                        </div>
                      </div>

                      <p className="text-gray-600 text-sm mb-6 line-clamp-2">{job.description.substring(0, 150)}...</p>

                      <div className="flex items-center justify-between">
                        <Link to={`/job-details/${slugify(job.title)}`} className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-600 text-sm font-medium rounded-lg hover:bg-primary-600 hover:text-white transition-colors duration-200">
                          View Details
                        </Link>
                        <button className="p-2 text-gray-400 hover:text-primary-600 transition-colors duration-200">
                          <i className="far fa-bookmark"></i>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <i className="fas fa-search text-gray-400 text-4xl mb-4"></i>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">No jobs found</h3>
                    <p className="text-gray-600 mb-6">Try adjusting your filters to find what you're looking for.</p>
                    <button 
                      onClick={resetFilters}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700">
                      Reset Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Alert Section */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Get Job Alerts in Your Email</h2>
              <p className="text-xl text-primary-100 max-w-2xl mx-auto">Stay updated with the latest job opportunities matching your preferences</p>
            </div>
            <form className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input type="email"
                       placeholder="Your email address"
                       required
                       className="flex-1 px-4 py-3 border border-transparent rounded-lg focus:ring-2 focus:ring-white focus:border-transparent outline-none text-gray-900" />
                <button type="submit"
                        className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600">
                  Subscribe
                </button>
              </div>
            </form>
          </div>

          {/* Ad Banner */}
          <div className="ad-banner text-center mt-12">
            <iframe 
              border="0" 
              src="https://www.trip.com/partners/ad/SB5794672?Allianceid=6648580&SID=228113005&trip_sub1=" 
              style={{width:'300px', height:'250px'}} 
              frameBorder="0" 
              scrolling="no" 
              title="Trip.com Advertisement"
            ></iframe>
          </div>
        </div>
      </section>
    </>
  );
};

export default Jobs;